/**
 * Canonical staff role names and their short claim codes.
 *
 * CSW-1637 acceptance criteria: role names and their short claim codes must be
 * defined in EXACTLY ONE file, imported by every reader/writer — the backend
 * code that stamps claims (capicheAdminApi) and any frontend guard/component
 * that interprets them. This is that file. Do not restate this shape as
 * string literals anywhere else.
 *
 * Codes are short (claim byte budget — see claims.ts) but deliberately
 * readable, not bit-packed. See CSW-1637 "Why the encoding changed": a
 * bitmask was tried and rejected in the 2026-08-14 design review — it bought
 * headroom nobody needs (the `overflow` fallback already covers that) at the
 * cost of being undebuggable in a raw token.
 *
 * Student memberships never carry roles — `roles` is a staff-only concept.
 *
 *  - `orgAdmin`     — runs the school: School settings, staff management,
 *                     student passwords. The new-model equivalent of the
 *                     legacy `orgAdmin` boolean claim.
 *  - `studentAdmin` — class creation, student visibility + management. Every
 *                     active teacher has it (a future view-only role would
 *                     not) — it is NOT redundant with `type: 'teacher'`.
 *  - `billingAdmin` — Stripe subscription, seat limits, invoices.
 */
export const ROLE_NAMES = ['orgAdmin', 'studentAdmin', 'billingAdmin'] as const;

export type RoleName = (typeof ROLE_NAMES)[number];

/** RoleName -> short claim code. */
export const ROLE_CODES: Record<RoleName, string> = {
  orgAdmin: 'oa',
  studentAdmin: 'sa',
  billingAdmin: 'ba',
};

const CODE_TO_ROLE: Record<string, RoleName> = Object.fromEntries(
  (Object.entries(ROLE_CODES) as [RoleName, string][]).map(([role, code]) => [code, role]),
);

export function isRoleName(value: string): value is RoleName {
  return Object.prototype.hasOwnProperty.call(ROLE_CODES, value);
}

export function roleToCode(role: RoleName): string {
  return ROLE_CODES[role];
}

/**
 * Decodes a single short code back to its role name. Returns undefined for
 * an unrecognised code rather than throwing — a live ID token can outlive a
 * role being renamed or removed, so a reader should degrade (drop the
 * unknown code), not crash.
 */
export function codeToRole(code: string): RoleName | undefined {
  return CODE_TO_ROLE[code];
}

/**
 * Capiche-level (non-org) platform vocabulary — grants on the product
 * itself, not on a school. Single source of truth: `userRights/{uid}.platform`
 * and the token's `platform` claim both draw their allowed strings from here,
 * and the admin portal renders one toggle per entry (CSW-1681).
 *
 * These are NOT short-coded — `platform` never approaches the claims byte
 * cap (a couple of strings on a handful of accounts), and readability in a
 * raw token matters more.
 *
 *  - `admin`     — unlocks the admin portal / growth view (was the
 *                  `capicheAdmin` claim, CSW-1540).
 *  - `developer` — reserved; nothing grants or reads it yet.
 *
 * To add one: add the string to the array AND its blurb to the matching
 * `*_DESCRIPTIONS` map (the `Record<…, string>` type makes that a compile
 * error to forget), then write the guard/component that calls
 * `claimHasPlatformRole` / `claimHasPlatformFeature`. The admin toggle and
 * its explainer appear automatically.
 */
export const PLATFORM_ROLES = ['admin', 'developer'] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

/** Capiche-level feature flags. `extensionBeta` = access to the beta build
 *  of the Capiche Chrome extension (same name as the legacy `extensionBeta`
 *  claim it replaces, CSW-1564 — a beta could exist on any surface, so the
 *  flag is named for its surface, not a generic "beta"). */
export const PLATFORM_FEATURES = ['extensionBeta'] as const;
export type PlatformFeature = (typeof PLATFORM_FEATURES)[number];

/**
 * Human-readable explainer for each grant — the single place this copy
 * lives, so the admin portal (and anywhere else) renders it rather than
 * re-inventing wording next to a checkbox. Keyed exhaustively: adding a
 * role/feature without a description here won't compile.
 */
export const PLATFORM_ROLE_DESCRIPTIONS: Record<PlatformRole, string> = {
  admin: 'Unlocks the Capiche admin portal.',
  developer: 'Reserved — not yet granted or read by anything.',
};

export const PLATFORM_FEATURE_DESCRIPTIONS: Record<PlatformFeature, string> = {
  extensionBeta: 'Access to the beta build of the Capiche Chrome extension.',
};

export function isPlatformRole(value: string): value is PlatformRole {
  return (PLATFORM_ROLES as readonly string[]).includes(value);
}

export function isPlatformFeature(value: string): value is PlatformFeature {
  return (PLATFORM_FEATURES as readonly string[]).includes(value);
}
